import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api-client';

// Types
interface Budget {
  id: string;
  code: string;
  organization_id: string;
  name: string;
  description?: string;
  fiscal_year: number;
  scope_type: 'global' | 'department' | 'location' | 'category' | 'custom';
  department_id?: string;
  location_id?: string;
  equipment_category_id?: string;
  start_date: string;
  end_date: string;
  total_amount: number;
  currency: string;
  allocated_labor: number;
  allocated_materials: number;
  allocated_external_services: number;
  allocated_equipment: number;
  allocated_other: number;
  spent_labor: number;
  spent_materials: number;
  spent_external_services: number;
  spent_equipment: number;
  spent_other: number;
  total_spent: number;
  committed_amount: number;
  available_amount: number;
  status: 'draft' | 'active' | 'frozen' | 'closed' | 'exceeded';
  alert_threshold_percentage: number;
  alert_triggered: boolean;
  adjustments?: Array<{
    date: string;
    amount: number;
    reason: string;
    adjusted_by: string;
    previous_total: number;
    new_total: number;
  }>;
  approved_by?: string;
  approved_at?: string;
  notes?: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by?: string;
}

interface BudgetTransaction {
  id: string;
  organization_id: string;
  budget_id: string;
  transaction_type: 'work_order' | 'purchase_order' | 'purchase_request' | 'stock_movement' | 'manual' | 'adjustment';
  reference_type?: 'work_order' | 'purchase_order' | 'purchase_request' | 'stock_movement' | 'manual';
  reference_id?: string;
  reference_code?: string;
  amount: number;
  category: 'labor' | 'materials' | 'external_services' | 'equipment' | 'other';
  transaction_status: 'pending' | 'committed' | 'spent' | 'cancelled' | 'refunded';
  description: string;
  notes?: string;
  transaction_date: string;
  created_at: string;
  created_by: string;
}

interface BudgetListParams {
  fiscalYear?: number;
  scopeType?: string;
  departmentId?: string;
  locationId?: string;
  categoryId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

interface BudgetListResponse {
  items: Budget[];
  total: number;
}

interface BudgetTransactionListResponse {
  items: BudgetTransaction[];
  total: number;
}

interface BudgetStats {
  total: number;
  byStatus: Record<string, number>;
  byScopeType: Record<string, number>;
  totalAllocated: number;
  totalSpent: number;
  totalCommitted: number;
  totalAvailable: number;
  utilizationRate: number;
  budgetsOverThreshold: number;
  budgetsExceeded: number;
}

// Query keys
export const budgetKeys = {
  all: ['budgets'] as const,
  lists: () => [...budgetKeys.all, 'list'] as const,
  list: (filters: BudgetListParams) => [...budgetKeys.lists(), filters] as const,
  details: () => [...budgetKeys.all, 'detail'] as const,
  detail: (id: string) => [...budgetKeys.details(), id] as const,
  stats: (fiscalYear?: number) => [...budgetKeys.all, 'stats', fiscalYear] as const,
  approachingLimit: (threshold?: number) => [...budgetKeys.all, 'approaching-limit', threshold] as const,
  exceeded: () => [...budgetKeys.all, 'exceeded'] as const,
  transactions: (budgetId: string) => [...budgetKeys.all, 'transactions', budgetId] as const,
};

// Hooks
export function useBudgetList(params: BudgetListParams = {}) {
  return useQuery({
    queryKey: budgetKeys.list(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (params.fiscalYear) searchParams.set('fiscal_year', params.fiscalYear.toString());
      if (params.scopeType) searchParams.set('scope_type', params.scopeType);
      if (params.departmentId) searchParams.set('department_id', params.departmentId);
      if (params.locationId) searchParams.set('location_id', params.locationId);
      if (params.categoryId) searchParams.set('category_id', params.categoryId);
      if (params.status) searchParams.set('status', params.status);
      if (params.limit) searchParams.set('limit', params.limit.toString());
      if (params.offset) searchParams.set('offset', params.offset.toString());

      const response = await apiClient.get<BudgetListResponse>(
        `/budgets?${searchParams.toString()}`
      );
      return response.data;
    },
  });
}

export function useBudgetDetail(id: string) {
  return useQuery({
    queryKey: budgetKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<{ budget: Budget }>(
        `/budgets/${id}`
      );
      return response.data.budget;
    },
    enabled: !!id,
  });
}

export function useBudgetStats(fiscalYear?: number) {
  return useQuery({
    queryKey: budgetKeys.stats(fiscalYear),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (fiscalYear) searchParams.set('fiscal_year', fiscalYear.toString());

      const response = await apiClient.get<BudgetStats>(
        `/budgets/stats?${searchParams.toString()}`
      );
      return response.data;
    },
  });
}

export function useBudgetsApproachingLimit(threshold: number = 80) {
  return useQuery({
    queryKey: budgetKeys.approachingLimit(threshold),
    queryFn: async () => {
      const response = await apiClient.get<BudgetListResponse>(
        `/budgets/approaching-limit?threshold=${threshold}`
      );
      return response.data;
    },
  });
}

export function useBudgetsExceeded() {
  return useQuery({
    queryKey: budgetKeys.exceeded(),
    queryFn: async () => {
      const response = await apiClient.get<BudgetListResponse>('/budgets/exceeded');
      return response.data;
    },
  });
}

export function useBudgetTransactions(budgetId: string) {
  return useQuery({
    queryKey: budgetKeys.transactions(budgetId),
    queryFn: async () => {
      const response = await apiClient.get<BudgetTransactionListResponse>(
        `/budgets/${budgetId}/transactions`
      );
      return response.data;
    },
    enabled: !!budgetId,
  });
}

export function useCreateBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Budget>) => {
      const response = await apiClient.post<{ budget: Budget }>(
        '/budgets',
        data
      );
      return response.data.budget;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.lists() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.stats() });
    },
  });
}

export function useUpdateBudget(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Budget>) => {
      const response = await apiClient.put<{ budget: Budget }>(
        `/budgets/${id}`,
        data
      );
      return response.data.budget;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.lists() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: budgetKeys.stats() });
    },
  });
}

export function useDeleteBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/budgets/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.lists() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.stats() });
    },
  });
}

export function useApproveBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ budget: Budget }>(
        `/budgets/${id}/approve`
      );
      return response.data.budget;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.lists() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: budgetKeys.stats() });
    },
  });
}

export function useFreezeBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ budget: Budget }>(
        `/budgets/${id}/freeze`
      );
      return response.data.budget;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.lists() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: budgetKeys.stats() });
    },
  });
}

export function useCloseBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ budget: Budget }>(
        `/budgets/${id}/close`
      );
      return response.data.budget;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.lists() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: budgetKeys.stats() });
    },
  });
}

export function useAdjustBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, adjustment, reason }: { id: string; adjustment: number; reason: string }) => {
      const response = await apiClient.post<{ budget: Budget }>(
        `/budgets/${id}/adjust`,
        { adjustment, reason }
      );
      return response.data.budget;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.lists() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: budgetKeys.stats() });
    },
  });
}

export function useAddBudgetTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ budgetId, data }: { budgetId: string; data: Partial<BudgetTransaction> }) => {
      const response = await apiClient.post<{ transaction: BudgetTransaction }>(
        `/budgets/${budgetId}/transactions`,
        data
      );
      return response.data.transaction;
    },
    onSuccess: (_, { budgetId }) => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.detail(budgetId) });
      queryClient.invalidateQueries({ queryKey: budgetKeys.transactions(budgetId) });
      queryClient.invalidateQueries({ queryKey: budgetKeys.stats() });
    },
  });
}
